// ProtectedRoute.tsx
import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import { fetchMyParishes, fetchUserInfo, logoutUser, resolveSelectedParishName } from 'redux/slices/user/userSlice';
import { appClient, authClient } from 'api/apiClient';
import {
    selectCanAccessPortal,
    selectMustChangePassword,
    selectPortalContextReady,
    selectStaffRolesContractReady,
} from 'redux/selectors/parishContext';
import { notify } from 'components/Common/Toast';
import MuiLoader from 'components/Common/Loader';
import routes from 'constants/routes';

interface ProtectedRouteProps {
    children?: React.ReactNode;
}

/**
 * Auth + portal access gate.
 * After tokens: load /me (+ /me/parishes for shell). Deny only after /me
 * successfully hydrates staffRoles and the user has no staff (≥ CLERK) role.
 * If mustChangePassword, redirect to change-password (no portal shell).
 * Transient /me or /me/parishes failures keep the session (loader + retry).
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = () => {
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const deniedRef = useRef(false);
    const { auth } = useSelector((state: RootState) => ({
        auth: state.auth?.data,
    }));
    const meHydrated = useSelector((state: RootState) => state.user.meHydrated);
    const staffRolesContractReady = useSelector(selectStaffRolesContractReady);
    const canAccessPortal = useSelector(selectCanAccessPortal);
    const mustChangePassword = useSelector(selectMustChangePassword);
    const portalContextReady = useSelector(selectPortalContextReady);
    const selectedParishId = useSelector((state: RootState) => state.user.selectedParishId);
    const selectedParishName = useSelector((state: RootState) => state.user.selectedParishName);
    const { accessToken, refreshToken } = auth || {};

    const meReady = meHydrated && staffRolesContractReady;

    useEffect(() => {
        if (accessToken) {
            authClient.setToken(accessToken, refreshToken);
            appClient.setToken(accessToken, refreshToken);
        }
    }, [accessToken, refreshToken]);

    useEffect(() => {
        if (!accessToken) {
            deniedRef.current = false;
            return;
        }
        deniedRef.current = false;
        dispatch(fetchUserInfo());
        dispatch(fetchMyParishes());
    }, [dispatch, accessToken]);

    useEffect(() => {
        if (!accessToken || meReady) return;
        const timer = window.setTimeout(() => {
            dispatch(fetchUserInfo());
            dispatch(fetchMyParishes());
        }, 3000);
        return () => window.clearTimeout(timer);
    }, [accessToken, meReady, dispatch]);

    useEffect(() => {
        if (!accessToken || !meReady || mustChangePassword || canAccessPortal || deniedRef.current) return;
        deniedRef.current = true;
        notify('You need a parish staff role to use the portal.', 'error');
        dispatch(logoutUser({ silent: true }));
    }, [accessToken, meReady, mustChangePassword, canAccessPortal, dispatch]);

    // Staff-only users (no ParishMembership) never get a name from /me or
    // /me/parishes — wait until both have hydrated, then fetch it once.
    useEffect(() => {
        if (!portalContextReady || !selectedParishId || selectedParishName) return;
        dispatch(resolveSelectedParishName());
    }, [portalContextReady, selectedParishId, selectedParishName, dispatch]);

    if (!accessToken) {
        return <Navigate to="/login" replace />;
    }

    if (!meReady) {
        return <MuiLoader loading absolute />;
    }

    if (mustChangePassword) {
        return <Navigate to={routes.account.changePassword} replace state={{ forcePasswordChange: true, from: location.pathname }} />;
    }

    if (!canAccessPortal) {
        return <MuiLoader loading absolute />;
    }

    return <Outlet />;
};

export default ProtectedRoute;
